import axiosInstance from "../../../../api/axiosConfig";
import type { Draft } from "../../../../api/types/draft";
import { handleAPIError } from "../../../../api/utils/errors";
import type { FormTypesResponse } from "../../workflows/forms";
import type {
  CreatedDraft,
  CreatedForm,
  DraftTypeOption,
  WsNode
} from "../types";

export type MovedCaseFile = {
  case_file_id: string;
  node: WsNode;
  draft: Draft;
};

const call = async <T>(operation: () => Promise<{ data: T }>): Promise<T> => {
  try {
    const response = await operation();
    return response.data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

/** `/api/ws/...`: the workspace tree, shaped like `FilesystemAPI`. */
export const WorkspaceTreeAPI = {
  /** Every live node in render order; the server seeds and reconciles first. */
  getTree(individualId: string) {
    return call<{ nodes: WsNode[] }>(() =>
      axiosInstance.get(`/ws/${individualId}/tree`)
    );
  },

  createFolder(individualId: string, name: string, parentId?: string | null) {
    return call<{ node: WsNode }>(() =>
      axiosInstance.post(`/ws/${individualId}/folder`, {
        name,
        parent_id: parentId ?? null
      })
    );
  },

  /** The New Draft menu: the case's suggested draft types, then Custom. */
  getDraftTypes(individualId: string) {
    return call<{ draft_types: DraftTypeOption[] }>(() =>
      axiosInstance.get(`/ws/${individualId}/draft-types`)
    );
  },

  /** Fill Forms' menu: the fillable catalog and the copies the case fills. */
  getFormTypes(individualId: string) {
    return call<FormTypesResponse>(() =>
      axiosInstance.get(`/ws/${individualId}/form-types`)
    );
  },

  /**
   * "Add Form": an unfilled copy of a catalog form in `parentId` (the Forms
   * folder when null). The case's copy with the same label comes back
   * instead (`reused`).
   */
  createForm(
    individualId: string,
    formNumber: string,
    label?: string | null,
    parentId?: string | null
  ) {
    return call<CreatedForm>(() =>
      axiosInstance.post(`/ws/${individualId}/form`, {
        form_number: formNumber,
        label: label ?? null,
        parent_id: parentId ?? null
      })
    );
  },

  /**
   * "New draft" in `parentId`: custom without a `draftType`; with one, the
   * case's empty draft of that type comes back (`reused`) when it has one.
   */
  createDraft(
    individualId: string,
    parentId?: string | null,
    title?: string | null,
    draftType?: string | null
  ) {
    return call<CreatedDraft>(() =>
      axiosInstance.post(`/ws/${individualId}/draft`, {
        parent_id: parentId ?? null,
        title: title ?? null,
        ...(draftType ? { draft_type: draftType } : {})
      })
    );
  },

  /**
   * Move Word case files into this folder as Custom drafts Casey can edit.
   * The case files are removed.
   */
  fromCaseFiles(
    individualId: string,
    body: {
      nodeIds: string[];
      parentId: string | null;
      beforeId?: string | null;
      afterId?: string | null;
    }
  ) {
    return call<{ moved: MovedCaseFile[] }>(() =>
      axiosInstance.post(`/ws/${individualId}/from-case-files`, {
        node_ids: body.nodeIds,
        parent_id: body.parentId,
        before_id: body.beforeId ?? null,
        after_id: body.afterId ?? null
      })
    );
  },

  /** Rename a folder, or a draft (writes the draft's title). */
  renameNode(individualId: string, nodeId: string, name: string) {
    return call<{ node: WsNode }>(() =>
      axiosInstance.patch(`/ws/${individualId}/node/${nodeId}`, { name })
    );
  },

  moveNodes(
    individualId: string,
    nodeIds: string[],
    targetParentId: string | null,
    position?: { beforeId?: string | null; afterId?: string | null }
  ) {
    return call<{ nodes: WsNode[] }>(() =>
      axiosInstance.post(`/ws/${individualId}/move`, {
        node_ids: nodeIds,
        target_parent_id: targetParentId,
        before_id: position?.beforeId ?? null,
        after_id: position?.afterId ?? null
      })
    );
  },

  /** Soft-deletes the nodes and subtrees; drafts and forms inside are deleted too. */
  deleteNodes(individualId: string, nodeIds: string[]) {
    return call<{ deleted: number }>(() =>
      axiosInstance.post(`/ws/${individualId}/delete`, { node_ids: nodeIds })
    );
  }
};
